import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ChevronLeft, Package, Ruler, FileText, Barcode, Sparkles, Image, AlertTriangle, CheckCircle2, X, Plus, Trash2, Upload, ArrowUp, ArrowDown, Lightbulb, TrendingUp, Settings2, Eye, ChevronDown } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { BottomNav } from '../components/BottomNav';
import { CategorySelector } from '../components/CategorySelector';

const SIMPLE_TABS = [
  { id: 'basic', label: 'Details', icon: Package },
  { id: 'images', label: 'Images', icon: Image },
  { id: 'ai', label: 'AI Content', icon: Sparkles },
];
const ADVANCED_TABS = [
  { id: 'basic', label: 'Basic', icon: Package },
  { id: 'specs', label: 'Specs', icon: Ruler },
  { id: 'tax', label: 'Tax', icon: FileText },
  { id: 'ids', label: 'IDs', icon: Barcode },
  { id: 'ai', label: 'AI Content', icon: Sparkles },
  { id: 'images', label: 'Images', icon: Image },
];
const ID_TYPES = ['GTIN', 'UPC', 'EAN', 'ISBN', 'Other', 'Not Applicable'];

const Field = ({ label, hint, children }) => (
  <div className="space-y-1">
    <label className="text-xs font-medium text-slate-600">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
  </div>
);

const Input = ({ value, onChange, type = 'text', placeholder, suffix, ...props }) => (
  <div className="relative">
    <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200 transition-colors"
      {...props} />
    {suffix && <span className="absolute right-3 top-2.5 text-xs text-slate-400">{suffix}</span>}
  </div>
);

const TextArea = ({ value, onChange, rows = 3, placeholder }) => (
  <textarea value={value ?? ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 outline-none focus:border-purple-400 resize-none" />
);

export const ProductEditScreen = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [advancedMode, setAdvancedMode] = useState(() => localStorage.getItem('sellgen_advanced') === 'true');
  const [nextActions, setNextActions] = useState([]);
  const [suggestingCompliance, setSuggestingCompliance] = useState(false);
  const [improvingListing, setImprovingListing] = useState(false);
  const [improvements, setImprovements] = useState(null);
  const [exportModal, setExportModal] = useState(null);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);

  useEffect(() => { fetchProduct(); }, [productId]);

  const fetchProduct = async () => {
    try {
      const res = await api.get(`/products/${productId}`);
      const p = res.data.product;
      setProduct(p);
      setForm({
        title: p.ai_title || '', description: p.ai_description || '',
        price: p.price || '', mrp: p.mrp || '', brand: p.brand || '',
        category: p.category || '', subcategory: p.subcategory || '',
        sku: p.sku || '', stock_quantity: p.stock_quantity ?? '',
        material: p.material || '', color: p.color || '', size: p.size || '',
        weight: p.weight || '', length: p.length || '', width: p.width || '', height: p.height || '',
        hsn_code: p.hsn_code || '', gst_rate: p.gst_rate ?? '',
        country_of_origin: p.country_of_origin || '', manufacturer: p.manufacturer || '',
        manufacturer_address: p.manufacturer_address || '',
        product_identifier_type: p.product_identifier_type || 'Not Applicable',
        product_identifier: p.product_identifier || '',
        ai_bullets: p.ai_bullets || [], ai_tags: p.ai_tags || p.tags || [],
      });
      fetchReadiness();
      fetchNextActions();
    } catch { navigate('/my-products'); }
  };

  const fetchReadiness = async () => {
    try {
      const res = await api.get(`/products/${productId}/readiness`);
      setReadiness(res.data);
    } catch {}
  };

  const fetchNextActions = async () => {
    try {
      const res = await api.get(`/products/${productId}/next-action`);
      setNextActions(res.data.actions || []);
    } catch {}
  };

  const updateField = (key, val) => { setForm(prev => ({ ...prev, [key]: val })); setDirty(true); };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.price = payload.price !== '' ? parseFloat(payload.price) || null : null;
      payload.mrp = payload.mrp !== '' ? parseFloat(payload.mrp) || null : null;
      payload.gst_rate = payload.gst_rate !== '' ? parseFloat(payload.gst_rate) || null : null;
      payload.stock_quantity = payload.stock_quantity !== '' ? parseInt(payload.stock_quantity) || null : null;
      const bullets = payload.ai_bullets;
      const tags = payload.ai_tags;
      delete payload.ai_bullets; delete payload.ai_tags;
      await api.put(`/products/${productId}`, payload);
      if (bullets || tags) {
        const extra = {};
        if (bullets) extra.ai_bullets = bullets;
        if (tags) extra.ai_tags = tags;
        await api.patch(`/products/${productId}/content`, extra);
      }
      setDirty(false);
      toast.success('Changes saved');
      fetchProduct();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const toggleMode = () => {
    const next = !advancedMode;
    setAdvancedMode(next);
    localStorage.setItem('sellgen_advanced', String(next));
    if (!next) setTab('basic');
  };

  // Image management
  const moveImage = async (from, to) => {
    const imgs = [...(product.images || [])];
    const order = imgs.map((_, i) => i);
    [order[from], order[to]] = [order[to], order[from]];
    try {
      const res = await api.post(`/products/${productId}/images/reorder`, { order });
      setProduct(p => ({ ...p, images: res.data.images }));
      toast.success('Images reordered');
    } catch (e) { toast.error('Reorder failed'); }
  };

  const deleteImage = async (idx) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/products/${productId}/images/${idx}`);
      fetchProduct();
      toast.success('Image deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const addImages = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('files', f));
    try {
      const res = await api.post(`/products/${productId}/images/add`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${res.data.added} image(s) added`);
      fetchProduct();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  // AI suggestions
  const suggestCompliance = async () => {
    setSuggestingCompliance(true);
    try {
      const res = await api.post(`/products/${productId}/suggest-compliance`, {}, { timeout: 60000 });
      const s = res.data.suggestions;
      if (s) {
        toast.success('AI suggestions ready');
        // Auto-fill empty fields only
        setForm(prev => ({
          ...prev,
          hsn_code: prev.hsn_code || s.hsn_code || '',
          gst_rate: prev.gst_rate || s.gst_rate || '',
          category: prev.category || s.category || '',
          subcategory: prev.subcategory || s.subcategory || '',
          country_of_origin: prev.country_of_origin || s.country_of_origin || '',
        }));
        setDirty(true);
        toast.info('AI Recommendation — Please verify before using for tax/compliance', { duration: 5000 });
      }
    } catch { toast.error('AI suggestion failed'); }
    setSuggestingCompliance(false);
  };

  const improveListing = async () => {
    setImprovingListing(true);
    try {
      const res = await api.post(`/products/${productId}/improve-listing`, {}, { timeout: 60000 });
      setImprovements(res.data.suggestions);
    } catch { toast.error('AI improvement failed'); }
    setImprovingListing(false);
  };

  const acceptImprovement = (key) => {
    if (!improvements) return;
    if (key === 'all') {
      setForm(prev => ({
        ...prev,
        title: improvements.title || prev.title,
        description: improvements.description || prev.description,
        ai_bullets: improvements.bullets || prev.ai_bullets,
        ai_tags: improvements.tags || prev.ai_tags,
      }));
      setImprovements(null);
      setDirty(true);
      toast.success('All improvements applied');
    } else {
      updateField(key, improvements[key]);
      toast.success(`${key} updated`);
    }
  };

  // Tags & bullets
  const addTag = () => { if (tagInput.trim() && !(form.ai_tags || []).includes(tagInput.trim())) { updateField('ai_tags', [...(form.ai_tags || []), tagInput.trim()]); setTagInput(''); } };
  const removeTag = (i) => updateField('ai_tags', (form.ai_tags || []).filter((_, idx) => idx !== i));
  const updateBullet = (i, v) => { const b = [...(form.ai_bullets || [])]; b[i] = v; updateField('ai_bullets', b); };
  const addBullet = () => updateField('ai_bullets', [...(form.ai_bullets || []), '']);
  const removeBullet = (i) => updateField('ai_bullets', (form.ai_bullets || []).filter((_, idx) => idx !== i));

  // Export with readiness check
  const handleExportCheck = (platform) => {
    const p = readiness?.platforms?.[platform];
    if (!p) { toast.error('Check readiness first'); return; }
    setExportModal({ platform, ...p });
  };

  if (!product) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><p className="text-sm text-slate-500">Loading...</p></div>;

  const ls = readiness?.listing_score;
  const platforms = readiness?.platforms;
  const tabs = advancedMode ? ADVANCED_TABS : SIMPLE_TABS;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="w-full max-w-lg mx-auto bg-white min-h-screen shadow-xl">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2 sticky top-0 z-20">
          <button onClick={() => navigate(-1)} data-testid="edit-back-btn"><ChevronLeft size={22} className="text-slate-600" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-slate-800 truncate" data-testid="edit-title">{form.title || 'Edit Product'}</h1>
          </div>
          <button onClick={toggleMode} className="p-1.5 rounded-lg bg-slate-100 text-slate-500" data-testid="mode-toggle" title={advancedMode ? 'Switch to Simple' : 'Switch to Advanced'}>
            {advancedMode ? <Eye size={16} /> : <Settings2 size={16} />}
          </button>
          <button onClick={saveChanges} disabled={saving || !dirty}
            className={`h-8 px-3 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors ${dirty ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-400'}`}
            data-testid="save-btn">
            <Save size={12} /> {saving ? '...' : 'Save'}
          </button>
        </div>

        {/* Next Best Action */}
        {nextActions.length > 0 && (
          <div className="mx-4 mt-3 space-y-1.5" data-testid="next-actions">
            {nextActions.slice(0, 2).map((a, i) => (
              <button key={i} onClick={() => {
                if (a.action === 'edit_product') setTab(a.tab || 'basic');
                else if (a.action === 'add_images') setTab('images');
                else if (a.action === 'generate_listing') navigate(`/content-studio/${productId}`);
                else if (a.action === 'export') navigate(`/preview/${productId}`);
                else if (a.action === 'improve_listing') improveListing();
              }}
                className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-colors ${a.priority <= 2 ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'}`}>
                {a.priority <= 2 ? <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" /> :
                 a.type === 'publish' ? <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" /> :
                 <TrendingUp size={14} className="text-blue-500 flex-shrink-0" />}
                <span className="text-xs text-slate-700 flex-1">{a.message}</span>
                <span className="text-[10px] text-purple-600 font-medium flex-shrink-0">Fix</span>
              </button>
            ))}
          </div>
        )}

        {/* Listing Score */}
        {ls && (
          <div className="mx-4 mt-3 p-3 bg-gradient-to-r from-slate-50 to-purple-50 rounded-xl border border-slate-200" data-testid="listing-score">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Listing Score</span>
              <span className={`text-base font-black ${ls.total_score >= 80 ? 'text-green-600' : ls.total_score >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{ls.total_score}</span>
            </div>
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${ls.total_score >= 80 ? 'bg-green-500' : ls.total_score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${ls.total_score}%` }} />
            </div>
          </div>
        )}

        {/* Marketplace Cards */}
        {platforms && (
          <div className="mx-4 mt-2 grid grid-cols-4 gap-1.5" data-testid="marketplace-readiness">
            {Object.entries(platforms).map(([name, p]) => (
              <button key={name} onClick={() => handleExportCheck(name)}
                className={`p-2 rounded-lg border text-center transition-colors ${p.ready ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}
                data-testid={`readiness-${name}`}>
                <p className="text-[9px] font-bold text-slate-600 capitalize">{name}</p>
                <p className={`text-sm font-black ${p.score >= 80 ? 'text-green-600' : p.score >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{p.score}%</p>
              </button>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="mx-4 mt-3 flex gap-1 overflow-x-auto pb-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors ${tab === t.id ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-500'}`}
              data-testid={`tab-${t.id}`}>
              <t.icon size={12} /> {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-4 space-y-4">

          {tab === 'basic' && (
            <>
              <Field label="Product Name"><Input value={form.title} onChange={v => updateField('title', v)} placeholder="What are you selling?" data-testid="field-title" /></Field>
              <Field label="Description"><TextArea value={form.description} onChange={v => updateField('description', v)} rows={3} placeholder="Tell buyers about your product" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Selling Price"><Input value={form.price} onChange={v => updateField('price', v)} type="number" placeholder="0" suffix="₹" /></Field>
                {advancedMode && <Field label="MRP"><Input value={form.mrp} onChange={v => updateField('mrp', v)} type="number" placeholder="0" suffix="₹" /></Field>}
              </div>
              <Field label="Brand"><Input value={form.brand} onChange={v => updateField('brand', v)} placeholder="Your brand name" data-testid="field-brand" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Category">
                  <button onClick={() => setShowCategoryPicker(true)}
                    className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-left flex items-center justify-between"
                    data-testid="category-picker-btn">
                    <span className={form.category ? 'text-slate-800' : 'text-slate-400'}>{form.category || 'Select category'}</span>
                    <ChevronDown size={14} className="text-slate-400" />
                  </button>
                  {form.subcategory && <p className="text-[10px] text-purple-600 mt-0.5">{form.subcategory}</p>}
                </Field>
                {advancedMode && <Field label="Subcategory"><Input value={form.subcategory} onChange={v => updateField('subcategory', v)} placeholder="e.g. Sarees" /></Field>}
              </div>
              {advancedMode && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="SKU"><Input value={form.sku} onChange={v => updateField('sku', v)} placeholder="Stock code" /></Field>
                  <Field label="Stock"><Input value={form.stock_quantity} onChange={v => updateField('stock_quantity', v)} type="number" placeholder="0" /></Field>
                </div>
              )}
              {!advancedMode && (
                <button onClick={toggleMode} className="w-full py-2 text-xs text-purple-600 font-medium bg-purple-50 rounded-lg" data-testid="show-advanced">
                  <Settings2 size={12} className="inline mr-1" /> Show Advanced Product Details
                </button>
              )}
            </>
          )}

          {tab === 'specs' && advancedMode && (
            <>
              <Field label="Material"><Input value={form.material} onChange={v => updateField('material', v)} placeholder="e.g. Cotton, Silk" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Color"><Input value={form.color} onChange={v => updateField('color', v)} placeholder="e.g. Blue" /></Field>
                <Field label="Size"><Input value={form.size} onChange={v => updateField('size', v)} placeholder="e.g. M, L" /></Field>
              </div>
              <Field label="Weight"><Input value={form.weight} onChange={v => updateField('weight', v)} placeholder="e.g. 500" suffix="grams" /></Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Length"><Input value={form.length} onChange={v => updateField('length', v)} placeholder="0" suffix="cm" /></Field>
                <Field label="Width"><Input value={form.width} onChange={v => updateField('width', v)} placeholder="0" suffix="cm" /></Field>
                <Field label="Height"><Input value={form.height} onChange={v => updateField('height', v)} placeholder="0" suffix="cm" /></Field>
              </div>
            </>
          )}

          {tab === 'tax' && advancedMode && (
            <>
              <button onClick={suggestCompliance} disabled={suggestingCompliance}
                className="w-full h-10 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
                data-testid="suggest-compliance-btn">
                <Lightbulb size={14} /> {suggestingCompliance ? 'AI Analyzing...' : 'AI Suggest HSN & GST'}
              </button>
              <Field label="HSN Code" hint="Harmonized code for GST. Ask AI if unsure.">
                <Input value={form.hsn_code} onChange={v => updateField('hsn_code', v)} placeholder="e.g. 62034200" data-testid="field-hsn" />
              </Field>
              <Field label="GST Rate" hint="Tax percentage (0, 5, 12, 18, or 28)">
                <Input value={form.gst_rate} onChange={v => updateField('gst_rate', v)} type="number" placeholder="e.g. 5" suffix="%" />
              </Field>
              <Field label="Country of Origin"><Input value={form.country_of_origin} onChange={v => updateField('country_of_origin', v)} placeholder="e.g. India" /></Field>
              <Field label="Manufacturer"><Input value={form.manufacturer} onChange={v => updateField('manufacturer', v)} placeholder="Manufacturer name" /></Field>
              <Field label="Manufacturer Address"><TextArea value={form.manufacturer_address} onChange={v => updateField('manufacturer_address', v)} rows={2} placeholder="Full address" /></Field>
            </>
          )}

          {tab === 'ids' && advancedMode && (
            <>
              <Field label="Product Barcode / Identifier Type" hint="Select if your product has a barcode">
                <select value={form.product_identifier_type} onChange={e => updateField('product_identifier_type', e.target.value)}
                  className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 outline-none">
                  {ID_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              {form.product_identifier_type !== 'Not Applicable' && (
                <Field label="Barcode Number" hint={`Enter your ${form.product_identifier_type} code. Add this if your marketplace requires it.`}>
                  <Input value={form.product_identifier} onChange={v => updateField('product_identifier', v)} placeholder={`Enter ${form.product_identifier_type}`} />
                </Field>
              )}
            </>
          )}

          {tab === 'ai' && (
            <>
              {/* Improve Listing Button */}
              <button onClick={improveListing} disabled={improvingListing}
                className="w-full h-11 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 transition-transform"
                data-testid="improve-listing-btn">
                <Sparkles size={16} /> {improvingListing ? 'AI Reviewing...' : 'Improve My Listing'}
              </button>

              {/* Improvement Preview */}
              {improvements && (
                <div className="bg-purple-50 rounded-xl p-3 border border-purple-200 space-y-3" data-testid="improvements-preview">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-purple-700">AI Suggestions</p>
                    <button onClick={() => setImprovements(null)} className="text-purple-400"><X size={14} /></button>
                  </div>
                  {improvements.improvements && (
                    <div className="space-y-1">
                      {improvements.improvements.map((imp, i) => <p key={i} className="text-[10px] text-purple-600">• {imp}</p>)}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button onClick={() => acceptImprovement('all')} className="flex-1 h-8 bg-purple-600 text-white rounded-lg text-xs font-bold" data-testid="accept-all-btn">Accept All</button>
                    <button onClick={() => setImprovements(null)} className="px-3 h-8 bg-white border border-purple-200 text-purple-600 rounded-lg text-xs">Reject</button>
                  </div>
                  {['title', 'description'].map(k => improvements[k] && (
                    <div key={k} className="bg-white rounded-lg p-2 border border-purple-100">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-slate-500 capitalize">{k}</span>
                        <button onClick={() => acceptImprovement(k === 'title' ? 'title' : k)} className="text-[10px] text-purple-600 font-medium">Use this</button>
                      </div>
                      <p className="text-xs text-slate-700 line-clamp-3">{improvements[k]}</p>
                    </div>
                  ))}
                </div>
              )}

              <Field label="Product Title"><Input value={form.title} onChange={v => updateField('title', v)} /></Field>
              <Field label="Description"><TextArea value={form.description} onChange={v => updateField('description', v)} rows={4} /></Field>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-600">Key Features</label>
                  <button onClick={addBullet} className="text-xs text-purple-600 flex items-center gap-0.5"><Plus size={12} /> Add</button>
                </div>
                {(form.ai_bullets || []).map((b, i) => (
                  <div key={i} className="flex gap-1.5 items-start">
                    <span className="text-[10px] text-slate-400 pt-2.5 w-4 flex-shrink-0">{i + 1}.</span>
                    <Input value={b} onChange={v => updateBullet(i, v)} placeholder={`Feature ${i + 1}`} />
                    <button onClick={() => removeBullet(i)} className="p-2 text-red-400 flex-shrink-0"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-slate-600">Search Tags</label>
                <div className="flex flex-wrap gap-1.5">
                  {(form.ai_tags || []).map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-[11px] font-medium">
                      {tag}<button onClick={() => removeTag(i)}><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={tagInput} onChange={setTagInput} placeholder="Add tag..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
                  <button onClick={addTag} className="h-10 px-3 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium flex-shrink-0">Add</button>
                </div>
              </div>
            </>
          )}

          {tab === 'images' && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">{(product.images || []).length}/10 images</p>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={addImages} />
                <button onClick={() => fileRef.current?.click()} disabled={(product.images || []).length >= 10}
                  className="h-8 px-3 bg-purple-600 text-white rounded-lg text-xs font-medium flex items-center gap-1 disabled:opacity-50"
                  data-testid="add-images-btn">
                  <Upload size={12} /> Add Images
                </button>
              </div>
              <div className="space-y-2">
                {(product.images || []).map((img, i) => (
                  <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2 border border-slate-200" data-testid={`image-row-${i}`}>
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-white border border-slate-200 flex-shrink-0">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${img.path || img}`} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {i === 0 ? <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">PRIMARY</span>
                               : <span className="text-[10px] text-slate-400">Image {i + 1}</span>}
                      {img.content_type && <p className="text-[9px] text-slate-400 mt-0.5">{img.content_type}</p>}
                    </div>
                    <div className="flex flex-col gap-1">
                      {i > 0 && <button onClick={() => moveImage(i, i - 1)} className="p-1 bg-white rounded border border-slate-200 text-slate-400"><ArrowUp size={12} /></button>}
                      {i < (product.images || []).length - 1 && <button onClick={() => moveImage(i, i + 1)} className="p-1 bg-white rounded border border-slate-200 text-slate-400"><ArrowDown size={12} /></button>}
                    </div>
                    {(product.images || []).length > 1 && (
                      <button onClick={() => deleteImage(i)} className="p-1.5 text-red-400" data-testid={`delete-image-${i}`}><Trash2 size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
              {(product.images || []).length < 3 && (
                <div className="flex gap-2 bg-amber-50 rounded-lg p-2.5 border border-amber-200">
                  <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-amber-700">Products with 3+ images perform significantly better on marketplaces.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Export Readiness Modal */}
        {exportModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" onClick={() => setExportModal(null)}>
            <div className="w-full max-w-lg bg-white rounded-t-2xl p-5 space-y-3 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="export-modal">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-800 capitalize">{exportModal.platform} Readiness</h3>
                <button onClick={() => setExportModal(null)}><X size={20} className="text-slate-400" /></button>
              </div>
              <div className="flex items-center gap-3">
                <div className={`text-2xl font-black ${exportModal.score >= 80 ? 'text-green-600' : 'text-amber-600'}`}>{exportModal.score}%</div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${exportModal.ready ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {exportModal.ready ? 'Ready to Export' : 'Needs Attention'}
                </span>
              </div>
              {exportModal.missing_required.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-bold text-red-600">Required</p>
                  {exportModal.missing_required.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-red-600"><X size={12} /> {f}</div>
                  ))}
                </div>
              )}
              {exportModal.warnings.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-bold text-amber-600">Recommendations</p>
                  {exportModal.warnings.map((w, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-amber-600"><AlertTriangle size={12} /> {w}</div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-2">
                {!exportModal.ready && (
                  <button onClick={() => { setExportModal(null); setTab('basic'); }}
                    className="flex-1 h-10 bg-purple-600 text-white rounded-lg text-sm font-bold">Fix Now</button>
                )}
                <button onClick={() => { setExportModal(null); navigate(`/preview/${productId}`); }}
                  className={`flex-1 h-10 rounded-lg text-sm font-bold ${exportModal.ready ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                  {exportModal.ready ? 'Export Now' : 'Export Anyway'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Category Picker */}
        {showCategoryPicker && (
          <CategorySelector
            value={form.category}
            subcategoryValue={form.subcategory}
            onSelect={(cat, sub) => {
              updateField('category', cat);
              updateField('subcategory', sub);
            }}
            onClose={() => setShowCategoryPicker(false)}
          />
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default ProductEditScreen;
